import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {NewDatatable} from '../../../../components/NewDataTable/NewDataTable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../../hooks/useModals'
import {GET, POST, PUT} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import styles from './ordersRoomService.module.css'
import {formatPhoneBR} from '../../../../utils/utils'
import * as XLSX from 'sheetjs-style'

// Types
export enum StatusOrderService {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'attending',
  CANCELLED = 'canceled',
  COMPLETED = 'completed',
}

export enum TypeOrderService {
  SERVICE_ROOM = 'service',
  SERVICE = 'service_extra',
}

interface SearchOrderServiceHotelDto {
  pluginspaceId?: number
  reservationId?: number
  statusId?: StatusOrderService
  type?: TypeOrderService
  startDate?: string
  endDate?: string
  hourStart?: string
  hourEnd?: string
}

interface OrderServiceHotel {
  id: number
  idExt: string
  pluginspaceId: number
  reservationId: number
  statusId: StatusOrderService
  content: {
    serviceName: string
    room: string
    serviceId: number | null
    formFields: Array<{
      formId: number
      type: string
      order: number
      label: string
      value: any
      category?: string
      selectedItems?: string[]
      items?: Array<{name: string; quantity: number}>
      selectedItem?: string
      quantity?: number
      gridId?: number | null
    }>
    short: string
  }
  customer?: {
    id: number
    name: string
    email: string
  }
  hotelReservation: {
    checkin: string
    checkout: string
    id: number
    roomId: number
  }
  customerPhones?: {numberPhone: string}[]
  type: TypeOrderService
  description?: string | null
  created: string
  updated?: string | null
  date?: string | null
  hour?: string | null
  priority?: string
  customerReviews?: string | null
  customerDescriptionReviews?: string | null
}

const OrdersRoomServiceContent: FC = () => {
  const {showWrapperModal, closeWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState<OrderServiceHotel[]>([])
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)
  const [filters, setFilters] = useState<SearchOrderServiceHotelDto>({})
  const [selectedOrder, setSelectedOrder] = useState<OrderServiceHotel | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [loadingStatusChange, setLoadingStatusChange] = useState(false)
  const [newStatus, setNewStatus] = useState<StatusOrderService | ''>('')
  const [modalType, setModalType] = useState<'details' | 'status' | null>(null)
  const [loadingExport, setLoadingExport] = useState(false)

  const getOrders = async (
    searchFilters?: SearchOrderServiceHotelDto,
    page: number = 1,
    take: number = 100,
    search: string = ''
  ) => {
    try {
      setLoading(true)

      const filtersToUse = searchFilters || filters

      // Constrói a URL com os parâmetros de paginação
      const queryParams = new URLSearchParams({
        page: page.toString(),
        take: take.toString(),
        ...(search && {search}),
      })

      const res = await POST(`/order-service-hotel/filter?${queryParams}`, filtersToUse)

      setOrders(res.data || [])
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_loading_orders') as string,
      })
    }
  }

  const handleFilterChange = (field: keyof SearchOrderServiceHotelDto, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSearch = () => {
    getOrders(filters)
  }

  const handleClearFilters = () => {
    setFilters({})
    getOrders({})
  }

  const formatOrderData = (data: OrderServiceHotel[]) => {
    return data.map((item) => ({
      ...item,
      statusLabel: getStatusLabel(item.statusId),
      priorityLabel: getPriorityLabel(item.priority || 'normal'),
      serviceName: item.content?.serviceName || '-',
      roomNumber: item.content?.room || '-',
      shortDescription: item.content?.short || '-',
      dateFormatted: item.date || '',
      hourFormatted: item.hour ? item.hour.substring(0, 5) : '-',
      createdFormatted: new Date(item.created).toLocaleString('pt-BR'),
    }))
  }

  const getStatusLabel = (status: StatusOrderService) => {
    const labels = {
      [StatusOrderService.PENDING]: i18n.t('orders_room_service.status_pending'),
      [StatusOrderService.CONFIRMED]: i18n.t('orders_room_service.status_confirmed'),
      [StatusOrderService.IN_PROGRESS]: i18n.t('orders_room_service.status_in_progress'),
      [StatusOrderService.CANCELLED]: i18n.t('orders_room_service.status_cancelled'),
      [StatusOrderService.COMPLETED]: i18n.t('orders_room_service.status_completed'),
    }
    return labels[status] || status
  }

  const getPriorityLabel = (priority: string) => {
    const labels = {
      low: i18n.t('service_requests.priority_low'),
      normal: i18n.t('service_requests.priority_normal'),
      high: i18n.t('service_requests.priority_high'),
      critical: i18n.t('service_requests.priority_critical'),
    }
    return labels[priority as keyof typeof labels] || priority
  }

  const getStatusBadgeClass = (status: StatusOrderService) => {
    const classes = {
      [StatusOrderService.PENDING]: 'bg-warning',
      [StatusOrderService.CONFIRMED]: 'bg-success',
      [StatusOrderService.IN_PROGRESS]: 'bg-primary',
      [StatusOrderService.CANCELLED]: 'bg-danger',
      [StatusOrderService.COMPLETED]: 'bg-info',
    }
    return classes[status] || 'bg-secondary'
  }

  // Função para buscar detalhes do pedido
  const getOrderDetails = async (orderId: number) => {
    try {
      // Abre o modal primeiro com loading
      setLoadingDetails(true)
      setModalType('details')
      showWrapperModal(i18n.t('orders_room_service.order_details') as string)

      const res = await GET(`/order-service-hotel/${orderId}`)
      setSelectedOrder(res.data)
      setLoadingDetails(false)
    } catch (e) {
      setLoadingDetails(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_loading_details') as string,
      })
    }
  }

  // Função para alterar status
  const changeOrderStatus = async () => {
    if (!selectedOrder || !newStatus) {
      Swal.fire({
        icon: 'warning',
        title: i18n.t('orders_room_service.warning') as string,
        text: i18n.t('orders_room_service.select_status_warning') as string,
      })
      return
    }

    try {
      setLoading(true)

      await PUT(`/order-service-hotel/${selectedOrder.id}/status`, {
        statusId: newStatus,
        pluginspaceId: selectedOrder.pluginspaceId,
        reservationId: selectedOrder.reservationId,
      })

      Swal.fire({
        icon: 'success',
        title: i18n.t('orders_room_service.success') as string,
        text: i18n.t('orders_room_service.success_status_changed') as string,
      })

      // Atualiza a lista de pedidos
      getOrders(filters)

      // Fecha o modal
      closeWrapperModal()
      setNewStatus('')
      setSelectedOrder(null)
      setModalType(null)

      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('orders_room_service.error') as string,
        text: i18n.t('orders_room_service.error_changing_status') as string,
      })
    }
  }

  // Função para mostrar modal de alteração de status
  const showStatusModal = (order: OrderServiceHotel) => {
    setSelectedOrder(order)
    setNewStatus('')
    setModalType('status')

    showWrapperModal(i18n.t('orders_room_service.change_status_title') as string)
  }

  useEffect(() => {
    getOrders()
  }, [])

  const exportToExcel = async () => {
    setLoadingExport(true)
    try {
      const dataToExport = formatOrderData(orders).map((item) => ({
        'ID Pedido': item.id,
        'ID Externo': item.idExt || '-',
        Serviço: item.serviceName,
        Quarto: item.roomNumber,
        'ID Reserva': item.reservationId,
        Status: item.statusLabel,
        Data: item.dateFormatted,
        Hora: item.hourFormatted,
        Prioridade: item.priorityLabel,
        'Valor Criado': item.createdFormatted,
      }))

      const ws = XLSX.utils.json_to_sheet(dataToExport)

      const maxWidth = 50
      const colWidths = Object.keys(dataToExport[0] || {}).map((key) => ({
        wch: Math.min(Math.max(key.length, 12), maxWidth),
      }))
      ws['!cols'] = colWidths

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Pedidos')

      const timestamp = new Date().toISOString().split('T')[0]
      XLSX.writeFile(wb, `pedidos-roomservice-${timestamp}.xlsx`)

      await Swal.fire({
        icon: 'success',
        title: String('Exportado com sucesso!'),
        text: String('O arquivo Excel foi baixado.'),
        confirmButtonText: String('OK'),
      })
    } catch (error) {
      console.error('Erro ao exportar:', error)
      await Swal.fire({
        icon: 'error',
        title: String('Erro ao exportar'),
        text: String('Ocorreu um erro ao gerar o arquivo Excel.'),
        confirmButtonText: String('OK'),
      })
    } finally {
      setLoadingExport(false)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('permissions.orders_room_service')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('orders_room_service.subtitle')}
                </span>
              </h3>
            </div>

            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.status')}
                    inputAttr={{
                      type: 'select',
                      placeholder: i18n.t('orders_room_service.select_status_placeholder'),
                    }}
                    value={filters.statusId || ''}
                    change={(value: StatusOrderService) =>
                      handleFilterChange('statusId', value || undefined)
                    }
                    options={[
                      {value: '', select: i18n.t('orders_room_service.all_status')},
                      {
                        value: StatusOrderService.PENDING,
                        select: i18n.t('orders_room_service.status_pending'),
                      },
                      {
                        value: StatusOrderService.CONFIRMED,
                        select: i18n.t('orders_room_service.status_confirmed'),
                      },
                      {
                        value: StatusOrderService.IN_PROGRESS,
                        select: i18n.t('orders_room_service.status_in_progress'),
                      },
                      {
                        value: StatusOrderService.CANCELLED,
                        select: i18n.t('orders_room_service.status_cancelled'),
                      },
                      {
                        value: StatusOrderService.COMPLETED,
                        select: i18n.t('orders_room_service.status_completed'),
                      },
                    ]}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.start_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('orders_room_service.start_date_placeholder'),
                    }}
                    value={filters.startDate || ''}
                    change={(value: string) => handleFilterChange('startDate', value || undefined)}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.end_date')}
                    inputAttr={{
                      type: 'date',
                      placeholder: i18n.t('orders_room_service.end_date_placeholder'),
                    }}
                    value={filters.endDate || ''}
                    change={(value: string) => handleFilterChange('endDate', value || undefined)}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.reservation_id')}
                    inputAttr={{
                      type: 'number',
                      placeholder: i18n.t('orders_room_service.reservation_id_placeholder'),
                    }}
                    value={filters.reservationId?.toString() || ''}
                    change={(value: string) =>
                      handleFilterChange('reservationId', value ? parseInt(value) : undefined)
                    }
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    label={i18n.t('orders_room_service.service_type')}
                    inputAttr={{
                      type: 'select',
                      placeholder: i18n.t('orders_room_service.select_service_type_placeholder'),
                    }}
                    value={filters.type?.toString() || ''}
                    change={(value: TypeOrderService) =>
                      handleFilterChange('type', value || undefined)
                    }
                    options={[
                      {value: '', select: i18n.t('orders_room_service.all_types')},
                      {
                        value: TypeOrderService.SERVICE_ROOM,
                        select: i18n.t('orders_room_service.service_forms'),
                      },
                      {
                        value: TypeOrderService.SERVICE,
                        select: i18n.t('orders_room_service.services'),
                      },
                    ]}
                  />
                </div>

                <div className='col-12 d-flex justify-content-end'>
                  <div className='d-flex gap-2'>
                    <Button
                      text={i18n.t('orders_room_service.search')}
                      color='primary'
                      size='small'
                      click={handleSearch}
                    />
                    <Button
                      text={i18n.t('orders_room_service.clear_filters')}
                      color='danger'
                      size='small'
                      click={handleClearFilters}
                    />
                    <Button
                      text={'Exportar Excel'}
                      color='success'
                      size='small'
                      click={exportToExcel}
                      icon='fas fa-download'
                      loading={loadingExport}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='card-body'>
              <NewDatatable
                data={formatOrderData(orders)}
                headers={[
                  {
                    key: i18n.t('orders_room_service.service'),
                    value: 'serviceName',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.room'),
                    value: 'roomNumber',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.reservation'),
                    value: 'reservationId',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.status'),
                    value: 'statusLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.date'),
                    value: 'dateFormatted',
                    type: 'date',
                  },
                  {
                    key: i18n.t('orders_room_service.time'),
                    value: 'hourFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.priority'),
                    value: 'priorityLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.created_at'),
                    value: 'createdFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('orders_room_service.actions'),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item: any) => {
                      getOrderDetails(item.id)
                    },
                    title: i18n.t('orders_room_service.view_details'),
                  },
                  {
                    icon: 'fas fa-edit',
                    action: (item: any) => {
                      showStatusModal(item)
                    },
                    title: i18n.t('orders_room_service.change_status'),
                  },
                ]}
                color='primary'
                meta={meta}
                onChangeData={async (take: number, page: number, search: string, order?: any) => {
                  await getOrders(filters, page, take, search)
                }}
                search
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal para visualizar detalhes */}
      {modalType === 'details' && (
        <ModalWrapper
          confirmAction={() => {
            closeWrapperModal()
            setModalType(null)
            setSelectedOrder(null)
          }}
          textAction={i18n.t('orders_room_service.close')}
          noCloseButton={true}
          confirmButtomColor='danger'
          maxWidth={true}
          isFooterFixed={true}
        >
          {loadingDetails ? (
            <div className='text-center p-5'>
              <div
                className='spinner-border text-primary'
                role='status'
                style={{width: '3rem', height: '3rem'}}
              >
                <span className='sr-only'>{i18n.t('orders_room_service.loading')}</span>
              </div>
              <p className='mt-3 text-muted'>{i18n.t('orders_room_service.loading_details')}</p>
            </div>
          ) : selectedOrder ? (
            <div className='p-0'>
              {/* Hero Header */}
              <div className='p-4 my-4 border border-gray-300 rounded'>
                <div className='d-flex align-items-center justify-content-between flex-wrap'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-50px me-3'>
                      <div className='symbol-label bg-white'>
                        <i className='fas fa-concierge-bell text-primary fs-2'></i>
                      </div>
                    </div>
                    <div>
                      <h3 className='mb-1 fw-bold'>
                        {selectedOrder.content?.serviceName ||
                          i18n.t('orders_room_service.room_service')}
                      </h3>
                      <div className='text-white-75'>
                        {i18n.t('orders_room_service.order')}:{' '}
                        <i className='fas fa-hashtag me-1'></i>
                        {selectedOrder.idExt}
                      </div>
                    </div>
                  </div>
                  <div className='text-start'>
                    <span className={`badge fs-6 ${getStatusBadgeClass(selectedOrder.statusId)}`}>
                      <i className='fas fa-circle me-1' style={{fontSize: '0.5rem'}}></i>
                      {getStatusLabel(selectedOrder.statusId)}
                    </span>
                    <div className='text-white-75 small mt-1'>
                      <i className='fas fa-clock me-1'></i>
                      {new Date(selectedOrder.created).toLocaleString('pt-BR')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Body */}
              <div className='p-4'>
                {/* Quick Info Cards */}
                <div className='row g-3 mb-5'>
                  <div
                    className='col-md-4 cursor-pointer'
                    onClick={() =>
                      window.open(
                        `/hospitality/hotel-room/editar/${selectedOrder.hotelReservation.roomId}`,
                        '_blank'
                      )
                    }
                  >
                    <div className='card border-0 bg-light-success h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-door-open text-success fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.location')}
                        </h6>
                        <h4 className='text-success fw-bolder mb-0'>
                          {selectedOrder.content?.room || 'N/A'}
                        </h4>
                        <small className='text-muted'>
                          {i18n.t('orders_room_service.room_number')}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-4'>
                    <div className='card border-0 bg-light-primary h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-calendar-check text-primary fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.scheduled_date')}
                        </h6>
                        <h4 className='text-primary fw-bolder mb-0'>
                          {selectedOrder.date
                            ? (() => {
                                const [year, month, day] = selectedOrder.date.split('-')
                                return `${day}/${month}/${year}`
                              })()
                            : '_'}
                        </h4>
                        <small className='text-muted'>
                          {selectedOrder.hour
                            ? `${selectedOrder.hour.substring(0, 5)}`
                            : i18n.t('orders_room_service.no_time')}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div
                    className='col-md-4 cursor-pointer'
                    onClick={() =>
                      window.open(
                        `/hospitality/hotel-reservation/editar/${selectedOrder.reservationId}`,
                        '_blank'
                      )
                    }
                  >
                    <div className='card border-0 bg-light-info h-100'>
                      <div className='card-body text-center p-4'>
                        <i className='fas fa-user-tie text-info fs-2x mb-2'></i>
                        <h6 className='fw-bold text-gray-800 mb-1'>
                          {i18n.t('orders_room_service.reservation')}
                        </h6>
                        <h4 className='text-info fw-bolder mb-0'>#{selectedOrder.reservationId}</h4>
                        <small className='text-muted'>
                          {i18n.t('orders_room_service.reservation_id_label')}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                {selectedOrder.customer && (
                  <div className='card border-0 shadow-sm mb-5'>
                    <div className='card-header bg-light border-0 py-3'>
                      <h5 className='card-title mb-0 fw-bold text-gray-800'>
                        <i className='fas fa-user text-primary me-2'></i>
                        {i18n.t('orders_room_service.customer_info')}
                      </h5>
                    </div>
                    <div className='card-body py-4'>
                      <div className='row g-3'>
                        <div className='col-md-6'>
                          <div className='bg-light-primary rounded p-3 border-start border-primary border-5'>
                            <div className='d-flex align-items-center'>
                              <i className='fas fa-user-circle text-primary fs-2 me-3'></i>
                              <div>
                                <small className='text-muted fw-semibold'>
                                  {i18n.t('orders_room_service.full_name')}
                                </small>
                                <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                  {selectedOrder.customer.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className='bg-light-info rounded p-3 border-start border-info border-5'>
                            <div className='d-flex align-items-center'>
                              <i className='fas fa-envelope text-info fs-2 me-3'></i>
                              <div>
                                <small className='text-muted fw-semibold'>
                                  {i18n.t('orders_room_service.email')}
                                </small>
                                <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                  {selectedOrder.customer.email}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className='bg-light-success rounded p-3 border-start border-success border-5'>
                            <div className='d-flex align-items-center'>
                              <i className='fas fa-phone text-success fs-2 me-3'></i>
                              <div>
                                <small className='text-muted fw-semibold'>
                                  {i18n.t('orders_room_service.phone')}
                                </small>
                                <p className='mb-0 fw-bold text-gray-800 fs-6'>
                                  {selectedOrder.customerPhones &&
                                  selectedOrder.customerPhones.length > 0
                                    ? formatPhoneBR(
                                        selectedOrder.customerPhones[
                                          selectedOrder.customerPhones.length - 1
                                        ].numberPhone
                                      )
                                    : 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Customer Review */}
                {(selectedOrder.customerReviews || selectedOrder.customerDescriptionReviews) && (
                  <div className='card border-0 shadow-sm mb-5'>
                    <div className='card-header bg-light border-0 py-3'>
                      <h5 className='card-title mb-0 fw-bold text-gray-800'>
                        <i className='fas fa-star text-warning me-2'></i>
                        {i18n.t('orders_room_service.customer_review')}
                      </h5>
                    </div>
                    <div className='card-body py-4'>
                      {selectedOrder.customerReviews && (
                        <div className='mb-3'>
                          <div className='d-flex align-items-center mb-2'>
                            <small className='text-muted fw-semibold me-2'>
                              {i18n.t('orders_room_service.rating')}:
                            </small>
                            <div className='d-flex align-items-center'>
                              {Array.from({length: 10}, (_, i) => {
                                const rating = parseFloat(selectedOrder.customerReviews || '0')
                                const filled = i < Math.floor(rating)
                                return (
                                  <i
                                    key={i}
                                    className={`fas fa-star ${
                                      filled ? 'text-warning' : 'text-muted'
                                    } fs-4 me-1`}
                                  ></i>
                                )
                              })}
                              <span className='ms-2 fw-bold text-gray-800 fs-5'>
                                {parseFloat(selectedOrder.customerReviews).toFixed(1)}/10
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedOrder.customerDescriptionReviews && (
                        <div className='bg-light-warning rounded p-3 border-start border-warning border-5'>
                          <small className='text-muted fw-semibold d-block mb-2'>
                            {i18n.t('orders_room_service.review_comment')}:
                          </small>
                          <p className='mb-0 fw-semibold text-gray-700 fs-6'>
                            {selectedOrder.customerDescriptionReviews}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Service Summary */}
                {selectedOrder.content?.short && (
                  <div className='card border-0 shadow-sm mb-5'>
                    <div className='card-header bg-light border-0 py-3'>
                      <h5 className='card-title mb-0 fw-bold text-gray-800'>
                        <i className='fas fa-clipboard-list text-warning me-2'></i>
                        {i18n.t('orders_room_service.request_summary')}
                      </h5>
                    </div>
                    <div className='card-body py-4'>
                      <div className='bg-light-warning rounded p-3 border-start border-warning border-5'>
                        <p className='mb-0 fw-semibold text-gray-700 fs-6'>
                          {selectedOrder.content.short}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Service Details */}
                {selectedOrder.content?.formFields && selectedOrder.content.formFields.length > 0 && (
                  <div className='card border-0 shadow-sm mb-5'>
                    <div className='card-header bg-light border-0 py-3'>
                      <h5 className='card-title mb-0 fw-bold text-gray-800'>
                        <i className='fas fa-tasks text-info me-2'></i>
                        {i18n.t('orders_room_service.request_details')}
                      </h5>
                    </div>
                    <div className='card-body p-0'>
                      {selectedOrder.content.formFields
                        .sort((a, b) => a.order - b.order)
                        .map((field, index) => (
                          <div
                            key={index}
                            className={`p-4 ${index % 2 === 0 ? 'bg-white' : 'bg-light-primary'}`}
                          >
                            <div className='d-flex align-items-start'>
                              <div className='flex-shrink-0 me-3'>
                                <div className='symbol symbol-40px'>
                                  <div className='symbol-label bg-primary text-white fw-bold'>
                                    {field.order + 1}
                                  </div>
                                </div>
                              </div>
                              <div className='flex-grow-1'>
                                <h6 className='mb-2 fw-bold text-gray-800'>{field.label}</h6>
                                <div className='text-gray-600'>
                                  {(() => {
                                    switch (field.type) {
                                      case 'selectCheckBox':
                                        return field.selectedItems &&
                                          field.selectedItems.length > 0 ? (
                                          <div>
                                            <span
                                              className={`badge badge-light-info me-2 mb-2 ${styles['hover-link']}`}
                                              onClick={() =>
                                                window.open(
                                                  `/hospitality/structured-content/grid-hotel/edit/${field.gridId}`,
                                                  '_blank'
                                                )
                                              }
                                            >
                                              {field.category}
                                            </span>
                                            <div className='d-flex flex-wrap gap-1'>
                                              {field.selectedItems.map(
                                                (item: string, idx: number) => (
                                                  <span key={idx} className='badge badge-success'>
                                                    {item}
                                                  </span>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.no_items_selected')}
                                          </div>
                                        )

                                      case 'selectCount':
                                        return field.items && field.items.length > 0 ? (
                                          <div>
                                            <span
                                              className={`badge badge-light-warning me-2 mb-3 ${styles['hover-link']}`}
                                              onClick={() =>
                                                window.open(
                                                  `/hospitality/structured-content/grid-hotel/edit/${field.gridId}`,
                                                  '_blank'
                                                )
                                              }
                                            >
                                              {field.category}
                                            </span>
                                            <div className='row g-2'>
                                              {field.items.map((item: any, idx: number) => (
                                                <div key={idx} className='col-md-6'>
                                                  <div className='d-flex justify-content-between align-items-center bg-white rounded p-2 border'>
                                                    <span className='fw-semibold'>{item.name}</span>
                                                    <span className='badge badge-dark fs-7'>
                                                      {item.quantity}
                                                    </span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.no_items_selected')}
                                          </div>
                                        )

                                      case 'selectListQuantity':
                                        return field.selectedItem && field.quantity ? (
                                          <div className='bg-white rounded p-3 border'>
                                            <div className='d-flex justify-content-between align-items-center'>
                                              <span className='fw-semibold text-gray-700'>
                                                {field.selectedItem}
                                              </span>
                                              <span className='badge badge-primary fs-6'>
                                                {field.quantity}{' '}
                                                {i18n.t('orders_room_service.units')}
                                              </span>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.no_items_selected')}
                                          </div>
                                        )

                                      case 'dateHour':
                                        if (typeof field.value === 'object') {
                                          return (
                                            <div className='row g-2'>
                                              {field.value.date && (
                                                <div className='col-md-6'>
                                                  <div className='bg-white rounded p-2 border d-flex align-items-center'>
                                                    <i className='fas fa-calendar text-primary me-2'></i>
                                                    <span className='fw-semibold'>
                                                      {field.value.date}
                                                    </span>
                                                  </div>
                                                </div>
                                              )}
                                              {field.value.time && (
                                                <div className='col-md-6'>
                                                  <div className='bg-white rounded p-2 border d-flex align-items-center'>
                                                    <i className='fas fa-clock text-success me-2'></i>
                                                    <span className='fw-semibold'>
                                                      {field.value.time}
                                                    </span>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )
                                        }
                                        return (
                                          field.value || (
                                            <div className='text-muted fst-italic d-flex align-items-center'>
                                              <i className='fas fa-info-circle me-1'></i>
                                              {i18n.t('orders_room_service.not_informed')}
                                            </div>
                                          )
                                        )

                                      case 'date':
                                        return field.value ? (
                                          <div className='bg-white rounded p-2 border d-flex align-items-center w-fit'>
                                            <i className='fas fa-calendar text-primary me-2'></i>
                                            <span className='fw-semibold'>{field.value}</span>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.not_informed')}
                                          </div>
                                        )

                                      case 'hour':
                                        return field.value ? (
                                          <div className='bg-white rounded p-2 border d-flex align-items-center w-fit'>
                                            <i className='fas fa-clock text-success me-2'></i>
                                            <span className='fw-semibold'>{field.value}</span>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.not_informed')}
                                          </div>
                                        )

                                      case 'quantity':
                                        return field.value > 0 ? (
                                          <div className='d-flex align-items-center'>
                                            <span className='badge badge-success fs-4 px-3 py-2'>
                                              {field.value}
                                            </span>
                                            <span className='ms-2 text-muted'>
                                              {i18n.t('orders_room_service.units')}
                                            </span>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            Quantidade: 0
                                          </div>
                                        )

                                      default:
                                        return field.value ? (
                                          <div className='bg-white rounded p-2 border'>
                                            <span className='fw-semibold text-gray-700'>
                                              {field.value}
                                            </span>
                                          </div>
                                        ) : (
                                          <div className='text-muted fst-italic d-flex align-items-center'>
                                            <i className='fas fa-info-circle me-1'></i>
                                            {i18n.t('orders_room_service.not_informed')}
                                          </div>
                                        )
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Technical Information */}
                <div className='card border-0 shadow-sm mb-4'>
                  <div className='card-header bg-light border-0 py-3'>
                    <h5 className='card-title mb-0 fw-bold text-gray-800'>
                      <i className='fas fa-cogs text-secondary me-2'></i>
                      {i18n.t('orders_room_service.technical_info')}
                    </h5>
                  </div>
                  <div className='card-body'>
                    <div className='row g-4'>
                      <div className='col-md-6'>
                        <div className='d-flex align-items-center p-3 bg-light-secondary rounded'>
                          <i className='fas fa-hashtag text-secondary fs-3 me-3'></i>
                          <div>
                            <small className='text-muted d-block mb-1'>
                              {i18n.t('orders_room_service.order')}
                            </small>
                            <span className='fw-bold fs-6'>#{selectedOrder.id}</span>
                          </div>
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className='d-flex align-items-center p-3 bg-light-primary rounded'>
                          <i className='fas fa-tag text-primary fs-3 me-3'></i>
                          <div>
                            <small className='text-muted d-block mb-1'>
                              {i18n.t('orders_room_service.type')}
                            </small>
                            <span className='badge badge-primary'>
                              {selectedOrder.type === TypeOrderService.SERVICE_ROOM
                                ? i18n.t('orders_room_service.service_forms')
                                : i18n.t('orders_room_service.services')}
                            </span>
                          </div>
                        </div>
                      </div>
                      {selectedOrder.content?.serviceId && (
                        <div
                          className={`col-md-6 ${styles['hover-link']}`}
                          onClick={() => {
                            if (selectedOrder.type === TypeOrderService.SERVICE_ROOM) {
                              window.open(
                                `/hospitality/structured-content/service-requests/edit/${selectedOrder.content?.serviceId}`,
                                '_blank'
                              )
                            } else if (selectedOrder.type === TypeOrderService.SERVICE) {
                              window.open(
                                `/hospitality/service-item/update/${selectedOrder.content?.serviceId}`,
                                '_blank'
                              )
                            }
                          }}
                        >
                          <div className='d-flex align-items-center p-3 bg-light-info rounded'>
                            <i className='fas fa-cog text-info fs-3 me-3'></i>
                            <div>
                              <small className='text-muted d-block mb-1'>
                                {selectedOrder.type === TypeOrderService.SERVICE_ROOM
                                  ? i18n.t('orders_room_service.service_form')
                                  : i18n.t('orders_room_service.services')}
                              </small>
                              <span className='fw-bold fs-6'>
                                {selectedOrder.content.serviceName}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedOrder.updated && (
                        <div className='col-md-6'>
                          <div className='d-flex align-items-center p-3 bg-light-warning rounded'>
                            <i className='fas fa-sync text-warning fs-3 me-3'></i>
                            <div>
                              <small className='text-muted d-block mb-1'>
                                {i18n.t('orders_room_service.last_update')}
                              </small>
                              <span className='fw-bold fs-7'>
                                {new Date(selectedOrder.updated).toLocaleString('pt-BR')}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Description */}
                {selectedOrder.description && (
                  <div className='card border-warning border-3 shadow-sm'>
                    <div className='card-header bg-light-warning border-0 py-3'>
                      <h5 className='card-title mb-0 fw-bold text-warning'>
                        <i className='fas fa-exclamation-triangle me-2'></i>
                        {i18n.t('orders_room_service.additional_notes')}
                      </h5>
                    </div>
                    <div className='card-body'>
                      <div className='bg-white rounded p-3 border-start border-warning border-5'>
                        <p className='mb-0 text-gray-700 fs-6'>{selectedOrder.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </ModalWrapper>
      )}

      {/* Modal para alterar status */}
      {modalType === 'status' && selectedOrder && (
        <ModalWrapper
          confirmAction={changeOrderStatus}
          textAction='Alterar Status'
          noCloseButton={false}
          confirmButtomColor='primary'
          disableMainButton={loadingStatusChange || !newStatus}
          isFooterFixed={true}
          onCloseAction={() => {
            setModalType(null)
            setSelectedOrder(null)
            setNewStatus('')
          }}
        >
          <div className='p-3'>
            <div className='mb-4'>
              <h6 className='fw-bold'>
                {i18n.t('orders_room_service.order')} #{selectedOrder.id}
              </h6>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.service')}:</strong>{' '}
                {selectedOrder.content?.serviceName}
              </p>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.room')}:</strong> {selectedOrder.content?.room}
              </p>
              <p className='mb-1'>
                <strong>{i18n.t('orders_room_service.current_status')}:</strong>
                <span className={`ms-2 badge ${getStatusBadgeClass(selectedOrder.statusId)}`}>
                  {getStatusLabel(selectedOrder.statusId)}
                </span>
              </p>
            </div>

            <Input
              label={i18n.t('orders_room_service.new_status')}
              inputAttr={{
                type: 'select',
                placeholder: i18n.t('orders_room_service.new_status_placeholder'),
                required: true,
              }}
              value={newStatus}
              change={(value: StatusOrderService) => setNewStatus(value)}
              options={[
                {value: '', select: i18n.t('orders_room_service.select_status')},
                {
                  value: StatusOrderService.PENDING,
                  select: i18n.t('orders_room_service.status_pending'),
                },
                {
                  value: StatusOrderService.CONFIRMED,
                  select: i18n.t('orders_room_service.status_confirmed'),
                },
                {
                  value: StatusOrderService.IN_PROGRESS,
                  select: i18n.t('orders_room_service.status_in_progress'),
                },
                {
                  value: StatusOrderService.CANCELLED,
                  select: i18n.t('orders_room_service.status_cancelled'),
                },
                {
                  value: StatusOrderService.COMPLETED,
                  select: i18n.t('orders_room_service.status_completed'),
                },
              ]}
              required={true}
            />
          </div>
        </ModalWrapper>
      )}
    </>
  )
}

const OrdersRoomService: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {
            title: i18n.t('orders_room_service.title'),
            path: 'orders-room-service',
            isActive: true,
          },
        ]}
      >
        {i18n.t('orders_room_service.title')}
      </PageTitle>
      <OrdersRoomServiceContent />
    </>
  )
}

export {OrdersRoomService}
